import { useMemo, useState } from "react";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, displayName, entityId, extractList, formatDate, getApiError } from "@/api/client";
import { ActionModal } from "@/components/ActionModal";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "@/components/PageStates";
import { StatusPill } from "@/components/StatusPill";
import { useAdminData } from "@/hooks/useAdminData";
import { cleanModerationPayload, inferReportRemoval, moderationFields, takedownModeMessage } from "@/utils/moderation";

const transform = (data) => extractList(data, ["reports"]);
const NO_FIELDS = [];
const reportedUserId = (report) =>
  report?.reported_user_id || report?.target_user_id || report?.user_id ||
  report?.target?.user_id || report?.target?.id ||
  report?.reported_user?.user_id || report?.reported_user?.id || report?.subject?.id;

export default function ReportsView() {
  const { data = [], loading, error, reload } = useAdminData("/admin/reports", { transform });
  const [dialog, setDialog] = useState(null); const [busy, setBusy] = useState(false); const [actionError, setActionError] = useState("");
  const fields = useMemo(() => moderationFields({ includeAction: true }), []);

  const resolve = async (values) => {
    setBusy(true); setActionError("");
    const removal = inferReportRemoval(dialog.report);
    const payload = dialog.childSafety
      ? cleanModerationPayload({ action: removal.action, reason: "child_safety", legal_hold: true, file_path: removal.file_path, note: "Child-safety / CSAE escalation" })
      : cleanModerationPayload(values);
    if (payload.action === "remove_file" && !String(payload.file_path || "").startsWith("auralive/")) {
      setActionError("File path must start with auralive/"); setBusy(false); return;
    }
    try {
      const { data: response } = await api.post(`/admin/reports/${entityId(dialog.report, "report_id")}/resolve`, payload);
      toast.success(response?.mode ? takedownModeMessage(response) : "Report resolved successfully");
      setDialog(null); reload();
    } catch (requestError) {
      const message = getApiError(requestError);
      const surfaced = requestError?.response?.status === 403 ? `Report remains open: ${message}` : message;
      setActionError(surfaced); toast.error(surfaced);
    } finally { setBusy(false); }
  };

  return (
    <div className="page">
      <PageHeader eyebrow="Safety queue" title="Reports" description="Review reports, remove harmful content, and preserve evidence when required." onRefresh={reload} loading={loading} />
      {loading ? <LoadingState /> : error ? <ErrorState message={error} onRetry={reload} /> : !data.length ? <EmptyState label="No reports are waiting for review." /> : (
        <div className="table-wrap"><table><thead><tr><th>Reporter</th><th>Reported user ID</th><th>Subject</th><th>Reason</th><th>Status</th><th>Received</th><th>Actions</th></tr></thead><tbody>
          {data.map((report, index) => {
            const id = entityId(report, "report_id") || index; const removal = inferReportRemoval(report); const targetUserId = reportedUserId(report);
            return <tr key={id} data-testid={`report-row-${id}`}><td>{displayName(report.reporter || report.reported_by)}</td><td className="mono-cell">{targetUserId ? <Link className="user-id-link" to={`/users?q=${encodeURIComponent(targetUserId)}`} data-testid={`reported-user-${id}-profile-link`}>{targetUserId}</Link> : "—"}</td><td>{displayName(report.target || report.reported_user || report.subject)}</td><td className="wide-cell">{report.reason || report.description || report.type || "—"}</td><td><StatusPill value={report.status || "open"} testId={`report-status-${id}`} /></td><td>{formatDate(report.created_at || report.timestamp)}</td><td><div className="report-actions"><button className="table-action" onClick={() => { setActionError(""); setDialog({ report, childSafety: false }); }} data-testid={`resolve-report-${id}-button`}><CheckCircle2 size={15} />Resolve</button>{removal && <button className="table-action child-safety-link" onClick={() => { setActionError(""); setDialog({ report, childSafety: true }); }} data-testid={`child-safety-report-${id}-button`}><ShieldAlert size={15} />CSAE</button>}</div></td></tr>;
          })}
        </tbody></table></div>
      )}
      <ActionModal
        open={!!dialog}
        title={dialog?.childSafety ? "Child-safety / CSAE escalation" : "Resolve report"}
        description={dialog?.childSafety ? "The reported content will be hidden and preserved under legal hold. This action is recorded in the audit trail." : "Choose an outcome and record any legal or moderation context."}
        confirmLabel={dialog?.childSafety ? "Escalate and preserve" : "Resolve report"}
        tone={dialog?.childSafety ? "danger" : "primary"}
        fields={dialog?.childSafety ? NO_FIELDS : fields}
        notice={dialog?.childSafety ? `Action: ${inferReportRemoval(dialog?.report)?.action?.replaceAll("_", " ")} · Reason: child_safety · Legal hold: enabled` : "Remove-file actions require an auralive/ object path."}
        error={actionError}
        onClose={() => { setDialog(null); setActionError(""); }}
        onConfirm={resolve}
        busy={busy}
      />
    </div>
  );
}